<template>
  <page-overlay @close="handleOverlayClose">
    <template slot="title">
      <span>{{ fileName }} · 分块管理</span>
      <a-tag :color="dataTypeConfig.color">{{ dataTypeConfig.label }}</a-tag>
      <a-tag class="ref-code-tag">{{ doc.refCode }}</a-tag>
    </template>

    <div class="segment-management">
      <!-- 文档信息卡 -->
      <div class="doc-head">
        <span class="doc-head__badge">{{ extBadge }}</span>
        <div class="doc-head__meta">
          <div class="doc-head__name">{{ fileName }}</div>
          <div class="doc-head__sub">
            所属知识库：{{ knowledge.name }}（ID: {{ knowledge.id }}）· 文档编号 #{{ doc.id }}
          </div>
          <div class="doc-head__cfg">
            <span>字符数：<b>{{ formatNum(doc.contentLength) }}</b></span>
            <span>Token 数：<b>{{ formatNum(doc.tokens) }}</b></span>
            <span>分片上限：<b>{{ doc.segmentMaxTokens }} tokens/片</b></span>
            <span>
              分块数：<b>{{ formatNum(stats.segmentCount) }}</b>
              <template v-if="stats.disabledSegmentCount">
                （停用 {{ stats.disabledSegmentCount }}）
              </template>
            </span>
            <span>累计召回：<b>{{ formatNum(stats.totalRetrievalCount) }}</b></span>
          </div>
        </div>
      </div>

      <div class="list-card">
        <a-form layout="inline">
          <a-form-item label="内容关键词：">
            <a-input
              v-model.trim="searchForm.keyword"
              placeholder="搜索分块内容"
              allow-clear
              style="width: 240px"
              @pressEnter="search"
            />
          </a-form-item>
          <a-form-item label="状态：">
            <a-select
              v-model="searchForm.status"
              placeholder="全部状态"
              style="width: 160px"
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            >
              <a-select-option v-for="item in statusOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
          <span>
            <a-button type="primary" @click="search" style="margin-top: 3px">
              <a-icon type="search" />查询
            </a-button>
            <a-button style="margin-left: 15px" @click="resetSearchForm">重置</a-button>
          </span>
        </a-form>

        <a-table
          :rowKey="(record, index) => record.id || index"
          :columns="table.columns"
          :data-source="table.dataInfo"
          :pagination="table.pagination.total ? table.pagination : false"
          :loading="table.loading"
          :scroll="{ x: 1320 }"
          @change="pageChange"
          bordered
          style="margin-top: 15px"
        >
          <span slot="content" slot-scope="text">
            <div class="seg-preview" :title="text">{{ text }}</div>
          </span>
          <span slot="contentLength" slot-scope="text">{{ formatNum(text) }}</span>
          <span slot="tokens" slot-scope="text">{{ formatNum(text) }}</span>
          <span slot="vectorId" slot-scope="text">
            <a-tag v-if="text" color="green">已向量化</a-tag>
            <a-tag v-else color="red">未同步</a-tag>
          </span>
          <span slot="retrievalCount" slot-scope="text">{{ formatNum(text) }}</span>
          <span slot="status" slot-scope="text">
            <a-tag :color="getStatusConfig(text).color">{{ getStatusConfig(text).label }}</a-tag>
          </span>
          <span slot="createTime" slot-scope="text">
            <div class="time-cell">{{ formatDate(text) }}</div>
            <div class="time-cell time-sub">{{ formatTime(text) }}</div>
          </span>
          <span slot="action" slot-scope="text, record" class="action">
            <a-switch
              :checked="record.status === 0"
              @change="(checked) => handleStatusChange(record, checked)"
            />
            <a-button class="btnClass" type="link" @click="openDetail(record)">详情</a-button>
          </span>
        </a-table>
      </div>
    </div>

    <!-- 分块详情面板（自定义面板，规避 a-drawer 动画/滚动锁偶发卡死问题） -->
    <div v-if="detailVisible" class="seg-detail-drawer">
      <div class="seg-detail-drawer__mask" @click="closeDetail"></div>
      <div class="seg-detail-drawer__panel">
        <div class="seg-detail-drawer__header">
          <span class="seg-detail-drawer__title">分块详情</span>
          <a-icon type="close" class="seg-detail-drawer__close" @click="closeDetail" />
        </div>
        <div class="seg-detail-drawer__body">
          <a-spin :spinning="detailLoading">
            <div class="detail-file">
              <span class="detail-file__badge">{{ extBadge }}</span>
              <div class="detail-file__info">
                <div class="cell-main">{{ fileName }}</div>
                <div class="cell-sub">分块编号 #{{ detail.id || "-" }}</div>
              </div>
            </div>

            <div class="kv-list">
              <div class="kv"><span class="k">所属知识库</span><span class="v">{{ knowledge.name }}（ID: {{ knowledge.id }}）</span></div>
              <div class="kv"><span class="k">所属文档</span><span class="v">{{ fileName }}（ID: {{ doc.id }}）</span></div>
              <div class="kv"><span class="k">字符数</span><span class="v num">{{ formatNum(detail.contentLength) }}</span></div>
              <div class="kv"><span class="k">Token 数</span><span class="v num">{{ formatNum(detail.tokens) }}</span></div>
              <div class="kv"><span class="k">向量编号</span>
                <span class="v">
                  <a-tag v-if="detail.vectorId" color="green" class="num">{{ detail.vectorId }}</a-tag>
                  <a-tag v-else color="red">未同步</a-tag>
                </span>
              </div>
              <div class="kv"><span class="k">召回次数</span><span class="v num">{{ formatNum(detail.retrievalCount) }}</span></div>
              <div class="kv"><span class="k">状态</span><span class="v">{{ detailStatusText }}</span></div>
              <div class="kv"><span class="k">创建时间</span><span class="v num">{{ detail.createTime || "-" }}</span></div>
            </div>

            <div class="detail-content-title">分块内容（检索单元）</div>
            <div class="detail-content-box">{{ detail.content || "（暂无内容）" }}</div>
          </a-spin>
        </div>
      </div>
    </div>
  </page-overlay>
</template>

<script>
import PageOverlay from "@/components/DSComponents/PageOverlay";
import {
  getSegmentPage,
  getSegmentDetail,
  updateSegmentStatus,
  getSegmentStats,
} from "@/api/intelligentCustomerService";
import { KNOWLEDGE_STATUS, KNOWLEDGE_STATUS_MAP } from "../../help";
import { dataTypeOfUrl, docFileName } from "./docUtils";

export default {
  name: "SegmentManagement",
  components: { PageOverlay },
  props: {
    doc: {
      type: Object,
      required: true,
    },
    knowledge: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      statusOptions: KNOWLEDGE_STATUS,
      searchForm: {
        keyword: "",
        status: "",
      },
      stats: {
        segmentCount: 0,
        disabledSegmentCount: 0,
        totalRetrievalCount: 0,
      },
      table: {
        loading: false,
        columns: [
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 90,
          },
          {
            title: "分块内容",
            dataIndex: "content",
            key: "content",
            scopedSlots: { customRender: "content" },
          },
          {
            title: "字符数",
            dataIndex: "contentLength",
            key: "contentLength",
            width: 100,
            scopedSlots: { customRender: "contentLength" },
          },
          {
            title: "Token 数",
            dataIndex: "tokens",
            key: "tokens",
            width: 100,
            scopedSlots: { customRender: "tokens" },
          },
          {
            title: "向量状态",
            dataIndex: "vectorId",
            key: "vectorId",
            width: 100,
            scopedSlots: { customRender: "vectorId" },
          },
          {
            title: "召回次数",
            dataIndex: "retrievalCount",
            key: "retrievalCount",
            width: 100,
            scopedSlots: { customRender: "retrievalCount" },
          },
          {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 90,
            scopedSlots: { customRender: "status" },
          },
          {
            title: "创建时间",
            dataIndex: "createTime",
            key: "createTime",
            width: 160,
            scopedSlots: { customRender: "createTime" },
          },
          {
            title: "操作",
            key: "action",
            width: 140,
            scopedSlots: { customRender: "action" },
          },
        ],
        dataInfo: [],
        pagination: {
          showSizeChanger: true,
          current: 1,
          pageSize: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        },
      },
      detailVisible: false,
      detailLoading: false,
      detail: {},
    };
  },
  computed: {
    fileName() {
      return docFileName(this.doc.url);
    },
    dataTypeConfig() {
      const map = {
        doc: { label: "文档", color: "blue" },
        image: { label: "图片", color: "purple" },
        link: { label: "网页链接", color: "green" },
      };
      return map[dataTypeOfUrl(this.doc.url)] || map.doc;
    },
    extBadge() {
      const name = String(this.doc.url || "").toLowerCase().split("/").pop().split("?")[0];
      const ext = name.includes(".") ? name.split(".").pop() : "";
      if (ext) return ext.slice(0, 4).toUpperCase();
      return dataTypeOfUrl(this.doc.url) === "link" ? "LINK" : "FILE";
    },
    detailStatusText() {
      const config = KNOWLEDGE_STATUS_MAP[this.detail.status];
      return config ? config.label : "-";
    },
  },
  mounted() {
    this.fetchList();
    this.fetchStats();
  },
  methods: {
    handleOverlayClose() {
      this.$emit("back");
    },
    isSuccess(res) {
      return res && [0, "0", 200, "200"].includes(res.code);
    },
    getStatusConfig(status) {
      return KNOWLEDGE_STATUS_MAP[status] || { label: "-", color: "default" };
    },
    formatNum(value) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toLocaleString("zh-CN");
    },
    formatDate(time) {
      if (!time) return "-";
      return String(time).split(" ")[0] || time;
    },
    formatTime(time) {
      if (!time) return "";
      const parts = String(time).split(" ");
      return parts[1] || "";
    },
    buildQueryParams() {
      const params = {
        documentId: this.doc.id,
        pageNo: this.table.pagination.current,
        pageSize: this.table.pagination.pageSize,
      };
      if (this.searchForm.keyword) {
        params.keyword = this.searchForm.keyword;
      }
      if (this.searchForm.status !== "" && this.searchForm.status !== undefined) {
        params.status = this.searchForm.status;
      }
      return params;
    },
    search() {
      this.table.pagination.current = 1;
      this.fetchList();
    },
    resetSearchForm() {
      this.table.pagination.current = 1;
      this.table.pagination.pageSize = 10;
      this.searchForm = this.$options.data.call(this).searchForm;
      this.fetchList();
    },
    pageChange(option) {
      this.table.pagination.current = option.current;
      this.table.pagination.pageSize = option.pageSize;
      this.fetchList();
    },
    async fetchList() {
      this.table.loading = true;
      try {
        const res = await getSegmentPage(this.buildQueryParams());
        if (this.isSuccess(res)) {
          if (Array.isArray(res.data)) {
            this.table.dataInfo = res.data;
            this.table.pagination.total = res.data.length;
          } else {
            this.table.dataInfo = res.data?.list || res.data?.records || [];
            this.table.pagination.total = res.data?.total || 0;
          }
        } else {
          this.$message.error(res?.msg || "获取分块列表失败");
        }
      } catch {
        this.$message.error("获取分块列表失败");
      } finally {
        this.table.loading = false;
      }
    },
    async fetchStats() {
      try {
        const res = await getSegmentStats(this.doc.id);
        if (this.isSuccess(res) && res.data) {
          this.stats = {
            segmentCount: res.data.segmentCount ?? 0,
            disabledSegmentCount: res.data.disabledSegmentCount ?? 0,
            totalRetrievalCount: res.data.totalRetrievalCount ?? 0,
          };
        }
      } catch {
        // 统计失败不阻塞列表
      }
    },
    handleStatusChange(record, checked) {
      const status = checked ? 0 : 1;
      const actionText = checked ? "启用" : "停用";
      const that = this;
      this.$confirm({
        title: `确认${actionText}分块 #${record.id}？`,
        content: checked ? "" : "停用后该分块将不参与检索。",
        onOk() {
          return updateSegmentStatus({ id: record.id, status })
            .then((res) => {
              if (that.isSuccess(res)) {
                that.$message.success(res.msg || `${actionText}成功`);
                that.fetchList();
                that.fetchStats();
              } else {
                that.$message.error(res?.msg || `${actionText}失败`);
              }
            })
            .catch(() => {
              that.$message.error(`${actionText}失败`);
            });
        },
      });
    },
    async openDetail(record) {
      this.detail = { ...record };
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const res = await getSegmentDetail(record.id);
        if (this.isSuccess(res) && res.data) {
          this.detail = res.data;
        }
      } catch {
        // 详情接口异常时保留行数据展示
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetail() {
      this.detailVisible = false;
      this.detail = {};
    },
  },
};
</script>

<style scoped lang="scss">
.segment-management {
  .doc-head {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .doc-head__badge {
    width: 44px;
    height: 44px;
    line-height: 44px;
    text-align: center;
    border-radius: 10px;
    background: #3b6cf6;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .doc-head__name {
    font-size: 16px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.85);
  }

  .doc-head__sub {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 4px;
  }

  .doc-head__cfg {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 7px;

    b {
      color: rgba(0, 0, 0, 0.65);
      font-weight: 600;
    }
  }

  .list-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    padding: 16px 18px;
  }

  // 操作列吸右：原生 sticky 单表格渲染（同文档管理页方案）
  ::v-deep .ant-table {
    th:last-child,
    td:last-child {
      position: sticky;
      right: 0;
      z-index: 1;
      background: #fff;
      border-left: 1px solid #e8e8e8;
    }

    th:nth-last-child(2),
    td:nth-last-child(2) {
      border-right: none;
    }

    th:last-child {
      z-index: 2;
      background: #fafafa;
    }

    tbody tr:hover td:last-child {
      background: #e6f7ff;
    }
  }

  .seg-preview {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 12.5px;
    line-height: 1.65;
    color: #3b4557;
    white-space: pre-line;
    word-break: break-all;
  }

  .time-cell {
    line-height: 1.4;
  }

  .time-sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .action {
    display: flex;
    align-items: center;
    gap: 8px;

    ::v-deep .ant-btn {
      padding: 0;
    }
  }
}

.ref-code-tag {
  font-family: monospace;
}

.cell-main {
  font-weight: 600;
  font-size: 13.5px;
}

.cell-sub {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 3px;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.num {
  font-variant-numeric: tabular-nums;
}

/* 分块详情面板（fixed 定位，无需考虑 portal） */
.seg-detail-drawer {
  .seg-detail-drawer__mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    background: rgba(15, 23, 42, 0.45);
  }

  .seg-detail-drawer__panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 560px;
    max-width: 92vw;
    z-index: 1000;
    background: #fff;
    box-shadow: -12px 0 40px rgba(15, 23, 42, 0.14);
    display: flex;
    flex-direction: column;
    animation: seg-drawer-in 0.22s ease;
  }

  .seg-detail-drawer__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    flex-shrink: 0;
    padding: 0 24px;
    border-bottom: 1px solid #f0f0f0;

    .seg-detail-drawer__title {
      font-size: 15px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.85);
    }

    .seg-detail-drawer__close {
      cursor: pointer;
      color: rgba(0, 0, 0, 0.45);
      font-size: 18px;
      padding: 4px;

      &:hover {
        color: rgba(0, 0, 0, 0.85);
      }
    }
  }

  .seg-detail-drawer__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 20px 24px;
  }
}

@keyframes seg-drawer-in {
  from {
    transform: translateX(40px);
    opacity: 0;
  }

  to {
    transform: none;
    opacity: 1;
  }
}

.detail-file {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;

  .detail-file__badge {
    width: 40px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    border-radius: 8px;
    background: #3b6cf6;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .detail-file__info {
    min-width: 0;
  }
}

.kv-list {
  margin-bottom: 18px;

  .kv {
    display: flex;
    padding: 8px 0;
    border-bottom: 1px solid #f0f3f8;
    font-size: 13px;

    .k {
      width: 90px;
      flex-shrink: 0;
      color: rgba(0, 0, 0, 0.45);
    }

    .v {
      flex: 1;
      min-width: 0;
      color: rgba(0, 0, 0, 0.75);
      word-break: break-all;
    }
  }
}

.detail-content-title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;
}

.detail-content-box {
  padding: 14px;
  background: #f7f9fc;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  max-height: 340px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-all;
  color: #3b4557;
}
</style>
